import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../../api/client'
import type { ProposalHeader } from '../../api/types'
import { useStores } from '../../app/StoreContext'
import { useMe } from '../../app/useMe'
import { Badge, Button, Card, Empty, ErrorBox, Field, Num, Spinner, formatDate, plural } from '../../ui/primitives'
import { GridHead, GridSummary, useGrid, type ColumnDef } from '../../ui/grid'
import { QuickDates, isoDate } from '../dashboard/QuickDates'
import { SupplierPicker } from '../../ui/SupplierPicker'
import { ThresholdProgress } from './ThresholdProgress'
import { channelHint, channelLabel, formatPacks, thresholdState } from './shared'
import type { ThresholdState } from './shared'

/**
 * Elenco delle proposte d'ordine aperte.
 *
 * Nell'applicazione attuale erano due elenchi separati, "Ordini Completi" e "Ordini Da Completare":
 * la domanda vera è sempre la stessa, quanto manca al minimo d'ordine. Qui l'elenco è uno solo e la
 * distanza dalla soglia è una colonna, così si vede in un colpo d'occhio anche a chi manca poco.
 *
 * L'avanzamento arriva già in percentuale nella testata della proposta: non si legge più
 * l'anagrafica fornitore per ricavare il minimo, e in tabella non compaiono importi.
 */

type QuickFilter = 'all' | 'complete' | 'incomplete'

const FILTERS: { key: QuickFilter; label: string }[] = [
  { key: 'all', label: 'Tutte' },
  { key: 'complete', label: 'Ordini Completi' },
  { key: 'incomplete', label: 'Ordini Da Completare' },
]

/**
 * Le letture per stato, con la regola decisa il 18/08/2026: una proposta con data antecedente a
 * oggi e non confermata è «scartata» — il giro nuovo la supera, nessuno la scarta a mano.
 * «Da confermare» sono le aperte di oggi.
 */
type View = 'toConfirm' | 'confirmed' | 'discarded' | 'all'

const VIEWS: { key: View; label: string }[] = [
  { key: 'toConfirm', label: 'Da confermare' },
  { key: 'confirmed', label: 'Confermate' },
  { key: 'discarded', label: 'Scartate' },
  { key: 'all', label: 'Tutte' },
]

interface Row {
  proposal: ProposalHeader
  state: ThresholdState
}

export function ProposalsPage() {
  const { selected, stores, storeFilter, storesReady, storeName } = useStores()
  const { hasPermission } = useMe()
  const navigate = useNavigate()
  const storeKey = selected.join(',')

  const [proposals, setProposals] = useState<ProposalHeader[]>()
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState<unknown>()

  const [filter, setFilter] = useState<QuickFilter>('all')
  const [supplierFilter, setSupplierFilter] = useState('')
  const [appliedSupplier, setAppliedSupplier] = useState('')

  // Vista di stato, finestra di date e ricerca articolo vivono anche nell'URL (v, da, a, art):
  // chi apre una proposta e torna indietro ritrova i filtri, e un indirizzo copiato li porta con sé.
  const [searchParams, setSearchParams] = useSearchParams()
  const [view, setView] = useState<View>(() => {
    const v = searchParams.get('v')
    return v === 'confirmed' || v === 'discarded' || v === 'all' ? v : 'toConfirm'
  })
  // Anche qui si parte su «Oggi» (chiesto il 19/08); le chiavi in URL — pure vuote, cioè
  // «Tutto» scelto apposta — vincono sul default.
  const [from, setFrom] = useState(() =>
    searchParams.has('da') ? (searchParams.get('da') ?? '') : isoDate(new Date()),
  )
  const [to, setTo] = useState(() =>
    searchParams.has('a') ? (searchParams.get('a') ?? '') : isoDate(new Date()),
  )
  const [itemText, setItemText] = useState(searchParams.get('art') ?? '')
  /** Il testo articolo interroga il server: si applica con Invio o dopo una pausa di battitura. */
  const [appliedItem, setAppliedItem] = useState(searchParams.get('art') ?? '')
  const itemTimer = useRef<number>(undefined)

  useEffect(() => () => window.clearTimeout(itemTimer.current), [])

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev)
        const setOrDel = (k: string, v: string) => (v ? p.set(k, v) : p.delete(k))
        setOrDel('v', view === 'toConfirm' ? '' : view)
        // Le date sempre, anche vuote: la chiave vuota è «Tutto» scelto dall'utente e il
        // default Oggi non deve scavalcarla al ritorno dal dettaglio.
        p.set('da', from)
        p.set('a', to)
        setOrDel('art', appliedItem)
        return p
      },
      { replace: true },
    )
  }, [view, from, to, appliedItem, setSearchParams])

  // `sync=true` allinea le proposte con il motore di previsione prima di rispondere: è la chiamata
  // lenta. La facciamo all'apertura e quando l'utente chiede di aggiornare, non a ogni filtro.
  const [syncNext, setSyncNext] = useState(true)
  const [reloadToken, setReloadToken] = useState(0)

  // «Tutta la rete» è nessuna selezione con più punti vendita visibili: chi ne ha uno solo
  // non seleziona niente, ma l'allineamento riguarda comunque quel punto vendita e deve girare.
  const allStores = selected.length === 0 && stores.length !== 1

  useEffect(() => {
    let cancelled = false
    // Con l'elenco dei punti vendita ancora in arrivo il filtro non è affidabile: si aspetta.
    if (!storesReady) return

    // Su "tutti i punti vendita" l'allineamento interrogherebbe il motore per l'intera rete e
    // richiederebbe minuti: lo facciamo solo su una selezione esplicita.
    const sync = syncNext && !allStores

    setLoading(true)
    setSyncing(sync)
    setError(undefined)

    api
      .get<ProposalHeader[]>('/order-proposals', {
        storeCode: storeFilter,
        supplierCode: appliedSupplier || undefined,
        view,
        from: from || undefined,
        to: to || undefined,
        itemText: appliedItem || undefined,
        sync,
      })
      .then((data) => {
        if (cancelled) return
        setProposals(data)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(e)
        setProposals(undefined)
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
        setSyncing(false)
      })

    if (syncNext) setSyncNext(false)

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeKey, storesReady, appliedSupplier, view, from, to, appliedItem, reloadToken])

  const rows = useMemo<Row[]>(() => {
    if (!proposals) return []

    // La percentuale vale per entrambi i canali: il server la calcola sul minimo giusto, in valore
    // per il fornitore diretto e in colli per il CEDI. Qui non serve distinguere.
    return proposals.map((proposal) => ({
      proposal,
      state: thresholdState(proposal.thresholdPercent),
    }))
  }, [proposals])

  /**
   * Lo stato come lo legge chi lavora: Confermata, Scartata (anche l'aperta con data passata,
   * per la regola del 18/08), oppure niente — e allora la colonna Stato parla di soglia.
   */
  const oggi = isoDate(new Date())
  const statoProposta = (p: ProposalHeader): { label: string; tone: 'ok' | 'muted' } | undefined =>
    p.status === 'Confirmed' || p.status === 'Dispatched' ? { label: 'Confermata', tone: 'ok' }
    : p.status === 'Discarded' ? { label: 'Scartata', tone: 'muted' }
    : p.forecastDate < oggi ? { label: 'Scartata (non confermata)', tone: 'muted' }
    : undefined

  const columns = useMemo<ColumnDef<Row>[]>(
    () => [
      { key: 'store', label: 'Punto vendita',
        // Si filtra anche per nome, non solo per codice: chi cerca «roma» non sa il codice.
        value: (r) => `${r.proposal.store} ${storeName(r.proposal.store)}` },
      { key: 'supplier', label: 'Fornitore',
        value: (r) => `${r.proposal.supplier} ${r.proposal.supplierName}` },
      { key: 'channel', label: 'Canale', value: (r) => channelLabel(r.proposal.channel) },
      { key: 'forecast', label: 'Data previsione', value: (r) => r.proposal.forecastDate },
      { key: 'lines', label: 'Righe', value: (r) => r.proposal.lineCount, numeric: true },
      { key: 'packs', label: 'Colli', value: (r) => r.proposal.totalPacks, numeric: true },
      { key: 'threshold', label: 'Verso il minimo d’ordine',
        value: (r) => r.proposal.thresholdPercent, numeric: true },
      {
        key: 'status',
        label: 'Stato',
        value: (r) =>
          statoProposta(r.proposal)?.label ??
          (r.state === 'reached' ? 'completo'
          : r.state === 'missing' ? 'da completare'
          : 'soglia non impostata'),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storeName, oggi],
  )

  const counts = useMemo(
    () => ({
      all: rows.length,
      complete: rows.filter((r) => r.state === 'reached').length,
      incomplete: rows.filter((r) => r.state === 'missing').length,
      unknown: rows.filter((r) => r.state === 'unknown').length,
    }),
    [rows],
  )

  const visible = useMemo(() => {
    if (filter === 'complete') return rows.filter((r) => r.state === 'reached')
    if (filter === 'incomplete') return rows.filter((r) => r.state === 'missing')
    return rows
  }, [rows, filter])

  // La griglia si applica a valle dei filtri rapidi in testata: quelle pastiglie scelgono
  // l'insieme, le colonne lo restringono.
  const grid = useGrid(visible, columns)

  const refresh = () => {
    setSyncNext(true)
    setReloadToken((t) => t + 1)
  }


  return (
    <>
      <div className="page-head">
        <div>
          <h1>Proposte d’ordine</h1>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            {allStores ?
              'Tutti i punti vendita visibili'
            : selected.length === 0 ?
              'Il tuo punto vendita'
            : `${plural(selected.length, 'punto vendita', 'punti vendita')} selezionat${selected.length === 1 ? 'o' : 'i'}`}{' '}
            ·{' '}
            {view === 'toConfirm' ? 'proposte da confermare'
            : view === 'confirmed' ? 'proposte confermate'
            : view === 'discarded' ? 'proposte scartate'
            : 'tutte le proposte'}
          </p>
        </div>

        <div className="page-tools">
          <div style={{ width: 260 }}>
            <SupplierPicker
              value={supplierFilter}
              placeholder="Filtra per fornitore"
              onChange={(code) => {
                setSupplierFilter(code)
                setAppliedSupplier(code)
              }}
            />
          </div>
          {hasPermission('proposals.bulkReview') && (
            <Button onClick={() => navigate('/proposals/bulk')}>Approvazione in blocco</Button>
          )}
          <Button onClick={refresh} disabled={loading}>
            {syncing ? 'Aggiornamento…' : 'Aggiorna'}
          </Button>
        </div>
      </div>

      {syncing && (
        <div className="notice notice-info">
          <strong>Allineamento con il motore di previsione in corso</strong>
          <p>
            Le proposte vengono ricalcolate prima di essere mostrate: la prima richiesta richiede
            qualche momento in più.
          </p>
        </div>
      )}

      {allStores && !syncing && (
        <div className="notice notice-info">
          <strong>Elenco non allineato con il motore di previsione</strong>
          <p>
            Senza un punto vendita selezionato le proposte vengono lette così come sono in archivio:
            l’allineamento sull’intera rete richiederebbe minuti. Scegli i punti vendita in alto e
            premi “Aggiorna” per ricalcolarle.
          </p>
        </div>
      )}

      <Card
        title="Elenco proposte"
        actions={
          <div className="inline">
            {FILTERS.map((f) => (
              <Button
                key={f.key}
                variant={filter === f.key ? 'primary' : 'default'}
                onClick={() => setFilter(f.key)}
              >
                {f.label} ({f.key === 'all' ? counts.all : f.key === 'complete' ? counts.complete : counts.incomplete})
              </Button>
            ))}
          </div>
        }
      >
        {/* Vista di stato: da confermare (le aperte di oggi), confermate, scartate — dove
            «scartata» è anche l'aperta con data passata mai confermata — oppure tutte. */}
        <div className="inline" style={{ marginBottom: 10 }}>
          {VIEWS.map((v) => (
            <Button
              key={v.key}
              variant={view === v.key ? 'primary' : 'ghost'}
              onClick={() => setView(v.key)}
            >
              {v.label}
            </Button>
          ))}
        </div>

        <div className="grid filter-grid">
          <Field label="Data previsione da">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="Data previsione a">
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Field
            label="Articolo (codice o descrizione)"
            hint="Mostra solo le proposte che contengono l'articolo."
          >
            <input
              value={itemText}
              placeholder="es. CATSAN oppure 00004905"
              onChange={(e) => {
                const v = e.target.value
                setItemText(v)
                window.clearTimeout(itemTimer.current)
                itemTimer.current = window.setTimeout(() => setAppliedItem(v.trim()), 600)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  window.clearTimeout(itemTimer.current)
                  setAppliedItem(itemText.trim())
                }
              }}
            />
          </Field>
          <div className="field field-actions">
            <QuickDates
              from={from}
              to={to}
              withYear
              allLabel="Tutto"
              onChange={(f, t) => {
                setFrom(f)
                setTo(t)
              }}
            />
          </div>
        </div>

        {loading && <Spinner label={syncing ? 'Allineamento e caricamento…' : 'Caricamento…'} />}

        {!loading && error !== undefined && <ErrorBox error={error} onRetry={refresh} />}

        {!loading && error === undefined && rows.length === 0 && (
          <Empty>
            Nessuna proposta
            {view === 'toConfirm' ? ' da confermare'
            : view === 'confirmed' ? ' confermata'
            : view === 'discarded' ? ' scartata'
            : ''}
            {appliedSupplier && ` per il fornitore ${appliedSupplier}`}
            {appliedItem && ` con l'articolo «${appliedItem}»`}
            {(from || to) && ' nel periodo scelto'}
            {allStores ? ' nei punti vendita visibili' : ' nei punti vendita selezionati'}. Le proposte
            compaiono quando il motore di previsione suggerisce un riordino; se ne attendi una, premi
            “Aggiorna” per rileggere il motore.
          </Empty>
        )}

        {!loading && error === undefined && rows.length > 0 && visible.length === 0 && (
          <Empty>
            Nessuna proposta in questo gruppo. Delle {counts.all} proposte aperte, {counts.complete} hanno
            raggiunto il minimo, {counts.incomplete} no e {counts.unknown} non hanno una soglia
            impostata.
          </Empty>
        )}

        {!loading && error === undefined && visible.length > 0 && (
          <div className="table-wrap">
            <table>
              <GridHead columns={columns} grid={grid} />
              <tbody>
                {grid.rows.map(({ proposal, state }) => (
                  <tr
                    key={proposal.id}
                    className="clickable"
                    onClick={() => navigate(`/proposals/${proposal.id}`)}
                  >
                    <td>
                      <div className="store-code">{proposal.store}</div>
                      <div className="muted">{storeName(proposal.store)}</div>
                    </td>
                    <td>
                      <div>{proposal.supplierName}</div>
                      <div className="muted">{proposal.supplier}</div>
                    </td>
                    <td title={channelHint(proposal.channel)}>
                      <Badge tone={proposal.channel === 'Cedi' ? 'info' : 'neutral'}>
                        {channelLabel(proposal.channel, proposal.sourceCedi)}
                      </Badge>
                    </td>
                    <td>{formatDate(proposal.forecastDate)}</td>
                    <td className="num">
                      <Num value={proposal.lineCount} />
                    </td>
                    <td className="num">{formatPacks(proposal.totalPacks)}</td>
                    <td>
                      <ThresholdProgress
                        percent={proposal.thresholdPercent}
                        missingLabel={
                          proposal.channel === 'Cedi' ?
                            'Soglia in colli non impostata per questo punto vendita'
                          : 'Soglia non impostata per questo fornitore'
                        }
                      />
                    </td>
                    <td>
                      {(() => {
                        const derivato = statoProposta(proposal)
                        if (derivato) return <Badge tone={derivato.tone}>{derivato.label}</Badge>
                        return state === 'reached' ?
                            <Badge tone="ok">Completo</Badge>
                          : state === 'missing' ?
                            <Badge tone="warn">Da completare</Badge>
                          : <Badge tone="muted">Soglia non impostata</Badge>
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <GridSummary grid={grid} total={visible.length} />
          </div>
        )}
      </Card>
    </>
  )
}
